import {
  Gem,
  Compass,
  Palette,
  Package,
  Users,
  MessageSquareWarning,
} from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Fase 5B — Hub de marca (Brand Strategy & Identity).
 * URL nueva: no sustituye ni redirige /servicios/rebranding-seo,
 * que conserva su intención de migración SEO hasta tener datos de GSC.
 * Evidencia: El Venado Foods (autorizado) y caso de bebidas anonimizado.
 */
export const brandingPage: MoneyPageContent = {
  id: "branding",
  icon: Gem,
  serviceType: "Brand Strategy and Identity",
  esPath: "/servicios/branding",
  enPath: "/en/services/branding",
  es: {
    metaTitle: "Branding estratégico: marca, identidad y packaging",
    metaDescription:
      "Branding como sistema: posicionamiento, identidad visual, packaging, aplicaciones digitales y marca para el mercado hispano de EE. UU. Consultoría de Luis ROC.",
    keywords: [
      "branding estratégico",
      "estrategia de marca",
      "identidad visual",
      "rebranding",
      "packaging",
      "branding mercado hispano",
    ],
    h1: "Branding: estrategia de marca, identidad y aplicaciones, no solo un logotipo",
    breadcrumbLabel: "Branding",
    heroSubtitle: "Qué representa la empresa, para quién y cómo se reconoce en cada punto de contacto",
    heroDescription:
      "El branding define qué significa una empresa para su mercado y cómo se traduce eso en identidad, packaging, punto de venta y presencia digital. Un logotipo es una pieza dentro de ese sistema, no el sistema completo. Trabajo la marca como una decisión de negocio: posicionamiento, público, promesa y aplicaciones que después deben funcionar en tienda, en Amazon, en Google y en la web.",
    ctaPrimary: { label: "Solicitar evaluación de marca", to: "/contacto" },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Branding no es diseño de logotipo",
        text: "Es el sistema de decisiones sobre posicionamiento, promesa, lenguaje visual y aplicaciones que hace reconocible a una empresa.",
      },
      {
        label: "Estrategia antes que estética",
        text: "Primero se define a quién se le habla, contra quién se compite y qué se promete; después se diseña.",
      },
      {
        label: "La marca vive en las aplicaciones",
        text: "Packaging, punto de venta, ficha de producto, sitio web y marketplaces son donde el cliente realmente encuentra la marca.",
      },
      {
        label: "Especialización en mercado hispano",
        text: "Marca pensada para el consumidor hispano de EE. UU., con localización cultural real y no traducción literal.",
      },
    ],
    explanation: {
      title: "Qué resuelve el branding (y qué no resuelve)",
      definition:
        "El branding es el trabajo de definir el significado de una empresa o producto en la mente de su mercado y expresarlo de forma consistente: posicionamiento, arquitectura de marca, identidad visual, tono y aplicaciones. Resuelve problemas de reconocimiento, coherencia y percepción de valor. No es un sustituto de la demanda: si nadie busca la categoría o el producto no compite, una marca mejor no lo corrige por sí sola.",
      whyImportant: {
        title: "Por qué importa comercialmente",
        text: "Cuando la marca es coherente, cada punto de contacto refuerza al anterior: el empaque sostiene lo que dice el anuncio, la ficha de producto sostiene lo que dice el empaque y el sitio sostiene lo que dice el vendedor. Cuando no lo es, cada canal empieza de cero.",
      },
      consequences: {
        title: "Qué pasa cuando no existe ese sistema",
        text: "Piezas que parecen de empresas distintas, un empaque que compite mal en anaquel, fichas de producto sin argumento propio y equipos internos que improvisan mensajes diferentes en cada canal.",
      },
    },
    problems: {
      title: "Situaciones de marca que trabajo con más frecuencia",
      subtitle: "Problemas concretos, no diagnósticos genéricos",
      items: [
        {
          icon: Compass,
          title: "Posicionamiento sin definir",
          description:
            "La empresa no puede explicar en una frase a quién sirve, contra quién compite y por qué la eligen.",
          impact: "Mensajes distintos en cada canal y decisiones de diseño sin criterio.",
        },
        {
          icon: Palette,
          title: "Identidad visual heredada e inconsistente",
          description:
            "Versiones del logotipo, colores y tipografías que fueron cambiando sin un sistema que las gobierne.",
          impact: "Menor reconocimiento y percepción de empresa improvisada.",
        },
        {
          icon: Package,
          title: "Packaging que no compite en el punto de venta",
          description:
            "Empaques que no comunican categoría, uso ni diferencia en los pocos segundos que dura la decisión en anaquel.",
          impact: "Producto correcto que pierde frente a alternativas peor hechas pero mejor presentadas.",
        },
        {
          icon: Users,
          title: "Marca que no conecta con el consumidor hispano",
          description:
            "Identidad y mensajes traducidos del inglés, sin considerar referencias culturales, ocasiones de consumo ni lenguaje real.",
          impact: "Una marca correcta en papel que el público objetivo no siente propia.",
        },
        {
          icon: MessageSquareWarning,
          title: "Rebranding hecho solo por gusto interno",
          description:
            "Cambio de identidad sin criterio de negocio, sin inventario de aplicaciones y sin plan para la presencia digital existente.",
          impact: "Costo alto, confusión en el mercado y riesgo de perder activos digitales ya posicionados.",
        },
      ],
    },
    methodology: {
      title: "Cómo trabajo un proyecto de marca",
      subtitle: "Mismo marco de trabajo del resto de la consultoría, aplicado a marca",
      steps: [
        {
          number: "01",
          title: "Descubrimiento del negocio",
          description:
            "Producto, márgenes, canales de venta, ciclo de compra, equipo interno y qué se espera que la marca resuelva.",
        },
        {
          number: "02",
          title: "Investigación de mercado y categoría",
          description:
            "Competencia directa, códigos visuales de la categoría, comportamiento del consumidor y, cuando aplica, diferencias del consumidor hispano.",
        },
        {
          number: "03",
          title: "Diagnóstico de la marca actual",
          description:
            "Inventario de aplicaciones existentes, inconsistencias, activos que conviene conservar y los que conviene retirar.",
        },
        {
          number: "04",
          title: "Estrategia y posicionamiento",
          description:
            "Público, promesa, territorio, arquitectura de marca y criterios que después gobiernan cada decisión visual.",
        },
        {
          number: "05",
          title: "Sistema de identidad",
          description:
            "Logotipo, color, tipografía, fotografía, iconografía y reglas de uso documentadas para que el equipo pueda aplicarlas.",
        },
        {
          number: "06",
          title: "Aplicaciones",
          description:
            "Packaging, material de punto de venta, plantillas, fichas de producto, presencia digital y ecommerce.",
        },
        {
          number: "07",
          title: "Implementación y ajuste",
          description:
            "Despliegue por prioridad comercial, revisión de cómo se aplica en la práctica y correcciones sobre el uso real.",
        },
      ],
      ctaText: "Solicitar evaluación de marca",
      ctaLink: "/contacto",
    },
    scope: {
      includes: {
        title: "Qué incluye un proyecto de marca",
        items: [
          "Estrategia de marca y posicionamiento",
          "Arquitectura de marca y nomenclatura de productos",
          "Sistema de identidad visual y reglas de uso",
          "Rebranding o refresco de marca según el diagnóstico",
          "Diseño de packaging y familias de producto",
          "Material de punto de venta y aplicaciones impresas",
          "Aplicaciones digitales: sitio, ecommerce, marketplaces y redes",
          "Adaptación cultural para el mercado hispano de EE. UU.",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Un logotipo suelto sin estrategia ni sistema",
          "Registro de marca ni asesoría legal de propiedad industrial",
          "Producción y fabricación de empaque",
          "Garantía de que un cambio de marca aumentará las ventas",
          "Migración técnica de SEO (se trabaja en su propia página de servicio)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién funciona",
        items: [
          "Empresas establecidas con producto probado y marca desordenada",
          "Fabricantes que venden a distribuidores y también a consumidor final",
          "Marcas de consumo que compiten en anaquel y en ecommerce",
          "Empresas B2B que quieren dejar de parecer más pequeñas de lo que son",
          "Compañías que entran al mercado hispano o al mercado de EE. UU.",
        ],
      },
      notFor: {
        title: "Para quién no funciona",
        items: [
          "Quien busca solo un logotipo rápido y económico",
          "Proyectos sin producto ni propuesta comercial definida",
          "Empresas que no pueden sostener la implementación posterior",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes sobre branding",
      items: [
        {
          question: "¿Branding y diseño de logotipo son lo mismo?",
          answer:
            "No. El logotipo es una pieza del sistema de identidad. El branding incluye posicionamiento, público, promesa, arquitectura de marca, lenguaje visual y las aplicaciones donde la marca se encuentra con el cliente.",
        },
        {
          question: "¿Necesito un rebranding completo o un refresco de marca?",
          answer:
            "Depende del diagnóstico. Si el posicionamiento sigue siendo válido y el problema es de consistencia y calidad de ejecución, un refresco suele bastar. Si cambió el público, la categoría o el modelo de negocio, el trabajo es de fondo.",
        },
        {
          question: "¿Cuánto cuesta un proyecto de marca?",
          answer:
            "Depende del alcance: número de aplicaciones, familias de producto, canales y si incluye packaging o presencia digital. Lo defino después de la reunión inicial, con un alcance escrito antes de empezar.",
        },
        {
          question: "¿Un rebranding puede afectar mi posicionamiento en Google?",
          answer:
            "Sí, cuando implica cambio de dominio, de estructura de URLs o de contenido. Por eso el trabajo de marca se coordina con la parte de migración SEO, que tiene su propia página y su propio plan de redirecciones.",
        },
        {
          question: "¿Qué significa adaptar una marca al mercado hispano?",
          answer:
            "Trabajar referencias culturales, ocasiones de consumo, lenguaje real y códigos visuales de la categoría para ese público. No es traducir textos ni agregar elementos decorativos.",
        },
      ],
    },
    related: {
      title: "Conexiones",
      subtitle: "Dónde continúa el trabajo de marca",
      links: [
        {
          label: "El Venado Foods: marca, packaging y ecommerce",
          description:
            "Caso documentado en Houston: rediseño de marca, identidad de producto Doña Chela, material de punto de venta, ecommerce y SEO local.",
          to: "/casos-de-exito/el-venado-foods-branding-ecommerce",
        },
        {
          label: "Marketing para el mercado hispano",
          description: "Cómo se traduce la estrategia de marca en búsqueda y contenido bilingüe.",
          to: "/servicios/seo-bilingue-mercado-hispano",
        },
        {
          label: "Rediseño de sitio web",
          description: "Cómo funciona esa marca digitalmente: arquitectura, conversión y desarrollo.",
          to: "/servicios/rediseno-web",
        },
        {
          label: "Rebranding y migraciones SEO",
          description: "Plan de URLs y redirecciones para no perder posicionamiento al cambiar de marca.",
          to: "/servicios/rebranding-seo",
        },
        {
          label: "Ecommerce y marketplaces",
          description: "Cómo se aplica la identidad en catálogo, ficha de producto y marketplaces.",
          to: "/servicios/ecommerce-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de branding estratégico de Luis ROC (ROC Worldwide Agency LLC): posicionamiento, identidad visual, rebranding, packaging, aplicaciones digitales y adaptación al mercado hispano de EE. UU. Evidencia documentada: El Venado Foods (Houston) y un proyecto de branding cultural para una empresa de bebidas de EE. UU. presentado de forma anónima.",
    keyPoints: [
      "Branding entendido como sistema estratégico, no como diseño de logotipo",
      "Incluye posicionamiento, identidad, packaging y aplicaciones digitales",
      "Especialización en marca para el consumidor hispano de Estados Unidos",
      "Se coordina con rediseño web y con migración SEO cuando cambia el dominio",
      "No se prometen resultados de ventas derivados de un cambio de marca",
    ],
  },
  en: {
    metaTitle: "Brand Strategy and Identity: branding beyond a logo",
    metaDescription:
      "Branding as a system: positioning, visual identity, packaging, digital applications and Hispanic market branding. Consulting by Luis ROC, ROC Worldwide Agency LLC.",
    keywords: [
      "brand strategy",
      "brand identity",
      "rebranding",
      "packaging design",
      "hispanic market branding",
      "brand positioning",
    ],
    h1: "Branding: brand strategy, identity and applications — not just a logo",
    breadcrumbLabel: "Branding",
    heroSubtitle: "What the company stands for, who it is for, and how it is recognized at every touchpoint",
    heroDescription:
      "Branding defines what a company means to its market and how that meaning shows up in identity, packaging, point of sale and digital presence. A logo is one piece of that system, not the system itself. I treat brand as a business decision: positioning, audience, promise and applications that then have to work in store, on Amazon, in Google and on the website.",
    ctaPrimary: { label: "Request a Brand Evaluation", to: "/en/contact" },
    ctaSecondary: { label: "See documented cases", to: "/en/case-studies" },
    summary: [
      {
        label: "Branding is not logo design",
        text: "It is the set of decisions about positioning, promise, visual language and applications that makes a company recognizable.",
      },
      {
        label: "Strategy before aesthetics",
        text: "First define who you speak to, who you compete against and what you promise; design comes after that.",
      },
      {
        label: "A brand lives in its applications",
        text: "Packaging, point of sale, product pages, website and marketplaces are where customers actually meet the brand.",
      },
      {
        label: "Hispanic market specialization",
        text: "Brands built for the U.S. Hispanic consumer through real cultural localization, not literal translation.",
      },
    ],
    explanation: {
      title: "What branding solves (and what it does not)",
      definition:
        "Branding is the work of defining what a company or product means to its market and expressing it consistently: positioning, brand architecture, visual identity, tone and applications. It solves problems of recognition, consistency and perceived value. It does not create demand: if nobody is looking for the category, or the product cannot compete, a better brand will not fix that on its own.",
      whyImportant: {
        title: "Why it matters commercially",
        text: "When the brand is coherent, every touchpoint reinforces the previous one: the package supports the ad, the product page supports the package, and the website supports the salesperson. When it is not, every channel starts from zero.",
      },
      consequences: {
        title: "What happens without that system",
        text: "Assets that look like they belong to different companies, packaging that loses on the shelf, product pages with no argument of their own, and internal teams improvising a different message in every channel.",
      },
    },
    problems: {
      title: "Brand situations I work on most often",
      subtitle: "Specific problems, not generic diagnostics",
      items: [
        {
          icon: Compass,
          title: "Undefined positioning",
          description:
            "The company cannot state in one sentence who it serves, who it competes against and why customers choose it.",
          impact: "A different message in every channel and design decisions with no criteria.",
        },
        {
          icon: Palette,
          title: "Inherited, inconsistent visual identity",
          description:
            "Logo versions, colors and typefaces that drifted over the years with no system governing them.",
          impact: "Lower recognition and the perception of an improvised company.",
        },
        {
          icon: Package,
          title: "Packaging that loses at point of sale",
          description:
            "Packages that fail to communicate category, use and difference in the few seconds a shelf decision takes.",
          impact: "A good product losing to worse alternatives that are better presented.",
        },
        {
          icon: Users,
          title: "A brand that does not connect with Hispanic consumers",
          description:
            "Identity and messaging translated from English, ignoring cultural references, consumption occasions and real language.",
          impact: "A brand that reads correctly on paper but that the target audience does not feel is theirs.",
        },
        {
          icon: MessageSquareWarning,
          title: "Rebranding driven only by internal taste",
          description:
            "An identity change with no business criteria, no inventory of applications and no plan for the existing digital presence.",
          impact: "High cost, market confusion and risk of losing digital assets that already rank.",
        },
      ],
    },
    methodology: {
      title: "How I run a brand project",
      subtitle: "The same consulting framework as the rest of the practice, applied to brand",
      steps: [
        {
          number: "01",
          title: "Business discovery",
          description:
            "Product, margins, sales channels, buying cycle, internal team and what the brand is expected to solve.",
        },
        {
          number: "02",
          title: "Market and category research",
          description:
            "Direct competitors, category visual codes, consumer behavior and, where relevant, how the Hispanic consumer differs.",
        },
        {
          number: "03",
          title: "Current brand diagnosis",
          description:
            "Inventory of existing applications, inconsistencies, assets worth keeping and assets worth retiring.",
        },
        {
          number: "04",
          title: "Strategy and positioning",
          description:
            "Audience, promise, territory, brand architecture and the criteria that govern every later design decision.",
        },
        {
          number: "05",
          title: "Identity system",
          description:
            "Logo, color, typography, photography, iconography and documented usage rules the team can actually apply.",
        },
        {
          number: "06",
          title: "Applications",
          description:
            "Packaging, point-of-sale material, templates, product pages, digital presence and ecommerce.",
        },
        {
          number: "07",
          title: "Rollout and adjustment",
          description:
            "Deployment by commercial priority, review of how it is applied in practice and corrections based on real use.",
        },
      ],
      ctaText: "Request a Brand Evaluation",
      ctaLink: "/en/contact",
    },
    scope: {
      includes: {
        title: "What a brand project includes",
        items: [
          "Brand strategy and positioning",
          "Brand architecture and product naming",
          "Visual identity system and usage rules",
          "Rebranding or brand refresh, depending on the diagnosis",
          "Packaging design and product families",
          "Point-of-sale material and printed applications",
          "Digital applications: website, ecommerce, marketplaces and social",
          "Cultural adaptation for the U.S. Hispanic market",
        ],
      },
      notIncludes: {
        title: "What it does not include",
        items: [
          "A standalone logo with no strategy or system",
          "Trademark registration or intellectual property advice",
          "Packaging production and manufacturing",
          "Any guarantee that a brand change will increase sales",
          "Technical SEO migration work, which has its own service page",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this works for",
        items: [
          "Established companies with a proven product and a disorganized brand",
          "Manufacturers selling both to distributors and to end consumers",
          "Consumer brands competing on the shelf and in ecommerce",
          "B2B companies that look smaller than they actually are",
          "Companies entering the Hispanic market or the U.S. market",
        ],
      },
      notFor: {
        title: "Who this does not work for",
        items: [
          "Anyone looking only for a fast, cheap logo",
          "Projects with no defined product or commercial proposition",
          "Companies that cannot sustain the rollout afterwards",
        ],
      },
    },
    faq: {
      title: "Branding FAQ",
      items: [
        {
          question: "Is branding the same as logo design?",
          answer:
            "No. The logo is one piece of the identity system. Branding covers positioning, audience, promise, brand architecture, visual language and the applications where the brand meets the customer.",
        },
        {
          question: "Do I need a full rebranding or a brand refresh?",
          answer:
            "It depends on the diagnosis. If the positioning still holds and the issue is consistency and execution quality, a refresh is usually enough. If the audience, category or business model changed, the work goes deeper.",
        },
        {
          question: "How much does a brand project cost?",
          answer:
            "It depends on scope: number of applications, product families, channels, and whether packaging or digital presence is included. I define it after the initial meeting, with a written scope before any work starts.",
        },
        {
          question: "Can rebranding affect my Google rankings?",
          answer:
            "Yes, when it involves a domain change, a URL structure change or new content. That is why brand work is coordinated with SEO migration, which has its own page and its own redirect plan.",
        },
        {
          question: "What does adapting a brand to the Hispanic market mean?",
          answer:
            "Working with cultural references, consumption occasions, real language and category visual codes for that audience. It is not translating copy or adding decorative elements.",
        },
      ],
    },
    related: {
      title: "Connections",
      subtitle: "Where brand work continues",
      links: [
        {
          label: "El Venado Foods: brand, packaging and ecommerce",
          description:
            "Documented case in Houston: brand redesign, Doña Chela product identity, point-of-sale material, ecommerce and local SEO.",
          to: "/en/case-studies/el-venado-foods-branding-ecommerce",
        },
        {
          label: "Hispanic market marketing",
          description: "How brand strategy turns into bilingual search and content work.",
          to: "/en/services/hispanic-market-seo",
        },
        {
          label: "Website redesign",
          description: "How that brand works digitally: architecture, conversion and development.",
          to: "/en/services/website-redesign",
        },
        {
          label: "Rebranding and SEO migration",
          description: "URL mapping and redirects so a brand change does not cost existing rankings.",
          to: "/en/services/rebranding-seo",
        },
        {
          label: "Ecommerce and marketplaces",
          description: "How identity applies to catalog, product pages and marketplaces.",
          to: "/en/services/ecommerce-seo",
        },
      ],
    },
    llmSummary:
      "Brand strategy and identity consulting by Luis ROC (ROC Worldwide Agency LLC): positioning, visual identity, rebranding, packaging, digital applications and U.S. Hispanic market branding. Documented evidence: El Venado Foods (Houston) and an anonymized cultural branding project for a U.S. beverage company.",
    keyPoints: [
      "Branding treated as a strategic system, not as logo design",
      "Covers positioning, identity, packaging and digital applications",
      "Specialization in branding for the U.S. Hispanic consumer",
      "Coordinated with website redesign and with SEO migration when the domain changes",
      "No sales results are promised as an outcome of a brand change",
    ],
  },
};
